import React, { ReactElement, ReactNode, useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { makeObservable, action, observable } from 'src_common/common/mobx-wrapper';

type LazyLoadWrapperProps = {
    children: ReactElement | ReactNode;
    id?: string;
    intersectionObserverOptions?: IntersectionObserverInit | undefined;
};

interface RefObserverType {
    ref: HTMLDivElement;
    observer: IntersectionObserver;
}

class LazyLoadWrapperState {
    @observable public isVisible: boolean;
    @observable private refObserver: RefObserverType | null = null;
    private intersectionObserverOptions: IntersectionObserverInit | undefined = { rootMargin: '50px' };

    public constructor(intersectionObserverOptions?: IntersectionObserverInit) {
        makeObservable(this);
        this.isVisible = false;
        this.intersectionObserverOptions = intersectionObserverOptions;
    }

    @action private setVisible = (): void => {
        this.isVisible = true;
    };

    @action public setRef = (ref: HTMLDivElement | null): void => {
        if (this.refObserver !== null) {
            this.refObserver.observer.unobserve(this.refObserver.ref);
            this.refObserver = null;
        }

        if (ref !== null) {
            const observer = new IntersectionObserver((entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting === true) {
                        this.setVisible();
                    }
                }
            }, this.intersectionObserverOptions);

            observer.observe(ref);

            this.refObserver = {
                ref,
                observer,
            };
        }
    };
}

/**
 * Prevents Layout Collapse on initial render.
 * Without a default height, all unrendered lazy wrappers have 0px height,
 * sit at the exact same scroll position, and trigger the IntersectionObserver
 * simultaneously when the first one enters the viewport.
 */
const LAZY_LOAD_INITIAL_PLACEHOLDER_HEIGHT_PX = 300;

export const LazyLoadWrapper = observer('LazyLoadWrapper', (props: LazyLoadWrapperProps): JSX.Element | null => {
    const [state] = useState(new LazyLoadWrapperState(props.intersectionObserverOptions));

    return (
        <div
            id={props.id}
            ref={state.setRef}
            style={{ minHeight: state.isVisible ? undefined : LAZY_LOAD_INITIAL_PLACEHOLDER_HEIGHT_PX }}
        >
            {state.isVisible ? <React.Suspense>{props.children}</React.Suspense> : null}
        </div>
    );
});
